import { IReport } from "../../helpers/Interfaces";
import RemoveFilter from "../../assets/images/RemoveFilter.svg";
import { convertDuration, divideCompletedStages, getPriority, validDateField, validStringField } from "../../helpers/HelperFunctions";
import ArrowRightIcon from "../../assets/images/ArrowRight.svg";
import { useCallback, useState } from "react";
import SearchableDown from "../../components/DropDown/DropDown";
import { ReportFilterTypes } from "../../helpers/Types";
import AccordionOpen from "../../assets/images/AccordionOpenBlack.svg";
import { Checkbox } from "@mui/material";
import { Colors } from "../../helpers/Colors";
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import Trash from "../../assets/images/Trash.svg";

type PendingReportsUiProps = {
    reports: IReport[];
    setSelectedReport: CallableFunction;
    openView: CallableFunction;
    filter: (filterBy: ReportFilterTypes, query: string) => void;
    cancelFilter: boolean;
    resetDefault: CallableFunction;
    setShowFilter?: CallableFunction
    tickReport: (reportId: string) => void;
    toggleAllReports: (reportIds: string[]) => void;
    isReportTicked: (reportId: string) => boolean;
    showDelete: boolean;
    areAllReportsTicked: (reportIds: string[]) => boolean;
}

export default function PendingReportsUI(props: Readonly<PendingReportsUiProps>) {

    const [selectedFilter, setSelectedFilter] = useState<ReportFilterTypes>("patientName");
    const [isDueDateClicked, setIsDueDateClicked] = useState(false);
    const [isAscending, setIsAscending] = useState(true);
    const [isDateofAccsessmentClicked, setDateofAccsessmentClicked] = useState(false);

    const handleClick = useCallback((selectedReport: IReport) => {
        props.setSelectedReport(selectedReport);
        props.openView("view");
    }, []);

    const handleSearchOpen = (filterBy: ReportFilterTypes) => {
        setSelectedFilter(filterBy);
    };

    const sortByDueDate = (): IReport[] => {
        const sortedReports: IReport[] = [...props.reports];

        sortedReports.sort((a, b) => {
            const dateA = a.dueDate ? new Date(a.dueDate) : new Date(0);
            const dateB = b.dueDate ? new Date(b.dueDate) : new Date(0);

            return isAscending ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
        });
        return sortedReports;
    };


    const sortBydateOfAssessment = (): IReport[] => {
        const sortedReports: IReport[] = [...props.reports];

        sortedReports.sort((a, b) => {
            const dateA = a.dateOfAssessment ? new Date(a.dateOfAssessment) : new Date(0);
            const dateB = b.dateOfAssessment ? new Date(b.dateOfAssessment) : new Date(0);

            return isAscending ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
        });
        return sortedReports;
    };

    const handleDueDateClick = () => {
        if (isDateofAccsessmentClicked) setDateofAccsessmentClicked(false);
        setIsAscending(!isAscending);
        setIsDueDateClicked(true);
        props.setShowFilter!(true);
    };

    const handleDateofAccessmentClick = () => {
        if (isDueDateClicked) setIsDueDateClicked(false);
        setIsAscending(!isAscending);
        setDateofAccsessmentClicked(true);
        props.setShowFilter!(true);
    };

    const handleClearFilter = () => {
        setIsDueDateClicked(false);
        setDateofAccsessmentClicked(false);
        props.resetDefault();
        props.setShowFilter!(false);
    };

    const handleSelectedItem = (selectedValue: string) => {
        props.filter(selectedFilter, selectedValue);
    };

    function getInitialValues() {
        let valueArr = [] as any[];
        props.reports.forEach(report => {
            if (report[selectedFilter] != null) {
                valueArr.push(report[selectedFilter])
            }
            else {
                valueArr.push('No Value')
            }
        });
        let distinctValues = valueArr.filter((value, index, array) => array.indexOf(value) === index);
        return distinctValues;
    }

    const sortedReports = isDueDateClicked
        ? sortByDueDate()
        : isDateofAccsessmentClicked
            ? sortBydateOfAssessment()
            : props.reports;

    return (<div className="table">
        <div className="header">
            <div className="header-row extra-small">
                <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                    checkedIcon={<img alt='check' src={BlueHomeDot} />}
                    value="cryon" style={{ color: Colors.white_10 }}
                    checked={props.areAllReportsTicked(sortedReports.map(report => report.id))}
                    onClick={(e) => { props.toggleAllReports(sortedReports.map(report => report.id)); e.stopPropagation(); }}
                />
            </div>
            <div className="header-row large">
                <span className="text">Patient Name</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={function (): void { handleSearchOpen("patientName") }} />
            </div>
            <div className="header-row medium">
                <span className="text">Report Type</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={function (): void { handleSearchOpen("reportType") }} />
            </div>
            <div className="header-row medium">
                <span className="text">Doctor</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={function (): void { handleSearchOpen("doctorName") }} />
            </div>
            <div className="header-row medium">
                <span className="text">Completed Stages</span>
            </div>
            <div className="header-row large clickable" onClick={() => handleDateofAccessmentClick()}>
                <span className="text">Date of Assessment</span>
                {isDateofAccsessmentClicked ? <img src={AccordionOpen} alt="SortArrow" className={isAscending ? "image" : "invert-image"} /> : <div />}
            </div>
            <div className="header-row large clickable" onClick={() => handleDueDateClick()}>
                <span className="text">Due Date</span>
                {isDueDateClicked ? <img src={AccordionOpen} alt="SortArrow" className={isAscending ? "image" : "invert-image"} /> : <div />}
            </div>
            <div className="header-row small">
                <span className="text">Audio Duration</span>
            </div>
            <div className="header-row medium">
                <span className="text">Urgency</span>
                <SearchableDown searchable={false} initialDetails={getInitialValues()} onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={function (): void { handleSearchOpen("priority") }} />
            </div>
            <div className="header-row extra-small justify-end gap">
                {props.cancelFilter
                    ? <img className="filter-icon" src={RemoveFilter} alt={"RemoveFilter"} onClick={handleClearFilter} />
                    : <div className="filter-icon" />
                }
                {props.showDelete
                    ? <img className="trash-icon" src={Trash} alt={"Trash"} onClick={() => { props.openView("bulkDelete"); }} />
                    : <div className="filter-icon" />
                }
            </div>
        </div>
        <div className="data">
            {sortedReports.map((report) => {
                return (<div key={report.id} className="data-row pending-padding"
                    onClick={() => { handleClick(report); }} >
                    <div className="cell text extra-small" >
                        <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                            checkedIcon={<img alt='check' src={BlueHomeDot} />}
                            value="cryon" style={{ color: Colors.white_10 }}
                            checked={props.isReportTicked(report.id)}
                            onClick={(e) => {
                                props.tickReport(report.id); e.stopPropagation();
                            }} />
                    </div>
                    <div className="cell text large">{validStringField(report.patientName)}</div>
                    <div className="cell text medium">{validStringField(report.reportType)}</div>
                    <div className="cell text medium">{validStringField(report.doctorName)}</div>
                    <div className="cell text medium">{divideCompletedStages(report.completedStages)}</div>
                    <div className="cell text large"><span className="date">{validDateField(report.dateOfAssessment)}</span></div>
                    <div className="cell text large"><span className="date">{validDateField(report.dueDate)}</span></div>
                    <div className="cell text small">{convertDuration(report.totalDuration)}</div>
                    <div className="cell text medium justify">{getPriority(report.priority)}</div>
                    <div className="cell text extra-small justify-end">
                        <img className="icon" src={ArrowRightIcon} alt={"ArrowRight"} />
                    </div>
                </div>)
            })}
        </div>
    </div>)
}